import './StyleGuide.scss';
import './TrainManager.scss';
import cn from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MdAdd, MdDelete, MdEdit } from 'react-icons/md';
import { InstanceRow } from '@blisspointmedia/bpm-types/dist/CopyTiger';
import { useFilteredList } from '@src/utils/search';
import { Button } from '../../Components/Button';
import { Appearance, Design, Intent, Size } from '../../Components/designConstants';
import { IconButton } from '../../Components/IconButton';
import { InteractiveDiv } from '../../Components/InteractiveEl';
import { FullPageSpinner } from '../../Components/Spinner';
import { useSetError } from '../../redux/modals';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { CopyTigerAlert } from './CopyTigerAlert';
import { Mixpanel, MxE } from '../../utils/mixpanelWrapper';
import { PAGE_NAMES } from '../../Components/Page/page-constants';
import {
  deleteCopyTigerModelInstance,
  DialogState,
  formatLastModified,
  getCopyTigerModelInstances,
  LANGUAGE_OPTIONS_MAP,
  SortBy,
  sortByKey,
  TableRow
} from './copyTigerUtils';
import { SearchSortBar } from './SearchSortBar';
import { TrainModal } from './TrainModal';

const TRAIN_MODAL = 'train_model';

interface TrainManagerProps {
  end: string;
  isLoadingTrainingOptions: boolean;
  onDateRangeChange: (dateRange: { start: string; end: string }) => void;
  start: string;
  trainingOptions: TableRow[];
}

export const TrainManager = ({
  end,
  isLoadingTrainingOptions,
  onDateRangeChange,
  start,
  trainingOptions
}: TrainManagerProps) => {
  const [isInitialLoadDone, setIsInitialLoadDone] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [modelInstances, setModelInstances] = useState<InstanceRow[]>();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedModelID, setSelectedModelID] = useState('');
  const [showTrainModal, setShowTrainModal] = useState(false);
  const [sortBy, setSortBy] = useState<SortBy>('a-to-z');
  const [trainModalMode, setTrainModalMode] = useState<'edit' | 'new'>('new');
  const [dialog, setDialog] = useState<DialogState>();

  const companyInfo = useCompanyInfo();
  const company = companyInfo.cid;
  const setError = useSetError();

  const filteredModels = useFilteredList(modelInstances ?? [], searchTerm, { keys: ['name'] });

  const sortedModels = useMemo(() => sortByKey(filteredModels, sortBy), [filteredModels, sortBy]);

  const selectedModel = useMemo(
    () => (modelInstances ?? []).find(model => model.id === selectedModelID),
    [modelInstances, selectedModelID]
  );

  const fetchModelInstances = useCallback(async () => {
    setIsLoading(true);
    try {
      const instances = await getCopyTigerModelInstances(company);
      setModelInstances(instances);
      if (instances.length && !isInitialLoadDone) {
        setSelectedModelID(instances[0].id);
      }
    } catch (e) {
      setIsInitialLoadDone(true);
      setModelInstances([]);
      setError({
        message: `Failed to fetch model instances. Error: ${e.message}`,
        reportError: e
      });
    } finally {
      setIsInitialLoadDone(true);
      setIsLoading(false);
    }
  }, [company, isInitialLoadDone, setError]);

  const onTrainModalClose = useCallback(
    (shouldRefresh = false) =>
      (async () => {
        setShowTrainModal(false);
        if (shouldRefresh) {
          await fetchModelInstances();
        }
      })(),
    [fetchModelInstances]
  );

  const createNewModel = useCallback(() => {
    setTrainModalMode('new');
    setShowTrainModal(true);
    Mixpanel.track(MxE.ModalOpened, { page: PAGE_NAMES.ADCOPY_AI, modal: TRAIN_MODAL });
  }, []);

  const editModel = useCallback((model: InstanceRow) => {
    setSelectedModelID(model.id);
    setTrainModalMode('edit');
    setShowTrainModal(true);
    Mixpanel.track(MxE.ModalOpened, { page: PAGE_NAMES.ADCOPY_AI, modal: TRAIN_MODAL });
  }, []);

  const deleteModel = useCallback(
    (model: InstanceRow) => {
      if (!model) return;
      setDialog({
        title: 'Confirm Deletion',
        body: `Are you sure you want to delete "${model.name}"? This action cannot be undone.`,
        intent: Intent.ERROR,
        onConfirm: async () => {
          try {
            setIsLoading(true);
            await deleteCopyTigerModelInstance(company, model.id);
            Mixpanel.track(MxE.RecordDeleted, { page: PAGE_NAMES.ADCOPY_AI, entity: 'model' });
            if (selectedModelID === model.id) {
              setSelectedModelID('');
            }
            onTrainModalClose(true);
            setDialog({
              title: 'Model Deleted',
              body: `"${model.name}" has been successfully deleted.`,
              intent: Intent.SUCCESS
            });
          } catch (e) {
            setError({
              message: `Failed to delete model instance. Error:${e.message}`,
              reportError: e
            });
          } finally {
            setIsLoading(false);
          }
        }
      });
    },
    [company, onTrainModalClose, selectedModelID, setError]
  );

  useEffect(() => {
    if (company && modelInstances == null) {
      fetchModelInstances();
    }
  }, [company, fetchModelInstances, modelInstances]);

  const sidebarList = useMemo(
    () => (
      <div className='copytiger-sidebar-list' data-testid='model-list'>
        {sortedModels.map(model => (
          <InteractiveDiv
            key={model.id}
            className={cn('copytiger-model-item', {
              'copytiger-model-selected': model.id === selectedModelID
            })}
            onClick={() => setSelectedModelID(model.id)}
          >
            <div className='copytiger-model-content'>
              <div className='copytiger-model-title-row'>
                <div className='copytiger-model-name'>{model.name}</div>
                <div className='copytiger-model-actions-sidebar'>
                  <IconButton
                    design={Design.NEUTRAL}
                    appearance={Appearance.EMPTY}
                    onClick={e => {
                      e.stopPropagation();
                      deleteModel(model);
                    }}
                    size={Size.SM}
                  >
                    <MdDelete />
                  </IconButton>
                  <IconButton
                    design={Design.NEUTRAL}
                    appearance={Appearance.EMPTY}
                    onClick={e => {
                      e.stopPropagation();
                      editModel(model);
                    }}
                    size={Size.SM}
                  >
                    <MdEdit />
                  </IconButton>
                </div>
              </div>
              <div className='copytiger-model-description-text'>
                {model.description ?? 'No description available'}
              </div>
              <div className='copytiger-model-meta-text'>
                Last updated on {formatLastModified(model.lastModified, model.lastUser)}
              </div>
            </div>
          </InteractiveDiv>
        ))}
      </div>
    ),
    [sortedModels, selectedModelID, deleteModel, editModel]
  );

  const detailsPanel = useMemo(() => {
    if (!selectedModel) {
      return (
        <div className='main-content'>
          <div className='text'>
            {(modelInstances ?? []).length ? 'Select' : 'Create'} a model instance to view its
            details
          </div>
        </div>
      );
    }
    return (
      <>
        <div className='copytiger-model-header'>
          <div className='copytiger-model-header-top'>
            <div className='copytiger-model-title-section'>
              <div className='copytiger-model-title-container'>
                <div className='copytiger-model-title'>{selectedModel.name}</div>
              </div>
            </div>
            <div className='copytiger-model-actions-header'>
              <IconButton
                design={Design.NEUTRAL}
                appearance={Appearance.EMPTY}
                onClick={() => deleteModel(selectedModel)}
                size={Size.SM}
              >
                <MdDelete />
              </IconButton>
              <IconButton
                design={Design.NEUTRAL}
                appearance={Appearance.EMPTY}
                onClick={() => editModel(selectedModel)}
                size={Size.SM}
              >
                <MdEdit />
              </IconButton>
            </div>
          </div>
          <div className='copytiger-model-description'>
            <div className='copytiger-description-text'>
              {selectedModel.description ?? 'No description available'}
            </div>
          </div>
          <div className='copytiger-model-meta-info'>
            <span className='copytiger-model-language'>
              Model Training Lanugage: {LANGUAGE_OPTIONS_MAP[selectedModel.language]}
            </span>
            <div className='copytiger-model-last-modified'>
              Last Modified {formatLastModified(selectedModel.lastModified, selectedModel.lastUser)}
            </div>
          </div>
        </div>
        <div className='main-content'>
          <div className='content-section'>
            <div className='content-display-wrapper'>
              <div className='content-display-content'>
                <div className='content-text copytiger-model-info'>
                  <div className='copytiger-info-section'>
                    <div className='copytiger-info-title'>Model Information</div>
                    <div className='text'>
                      <div className='bolded'>Model ID:</div> {selectedModel.id}
                    </div>
                    {selectedModel.adsUsed && (
                      <div className='text'>
                        <div className='bolded'>Training Data:</div> {selectedModel.adsUsed.length}{' '}
                        ads
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }, [selectedModel, modelInstances, deleteModel, editModel]);

  return (
    <>
      {(!isInitialLoadDone || isLoading) && <FullPageSpinner transparent={isInitialLoadDone} />}
      <div className='styleguide-container copytiger-train-manager' data-testid='train-manager'>
        <div className='bpm-styleguide'>
          <div className='styleguide-header'>
            <div className='header-text'>
              <div className='styleguide-title'>{companyInfo.name} Model Instances</div>
              <div className='styleguide-subtitle'>
                Manage your trained AdCopy AI models and training data.
              </div>
            </div>
            <div className='styleguide-header-actions'>
              <Button
                leadingElement={<MdAdd />}
                onClick={createNewModel}
                size={Size.SM}
                data-testid='add-model-button'
              >
                Add New Model
              </Button>
            </div>
          </div>
          <div className='styleguide-content'>
            <div className='styleguide-sidebar'>
              <SearchSortBar
                searchId='train-manager-search'
                searchValue={searchTerm}
                onSearchChange={setSearchTerm}
                searchAriaLabel='Search trained models'
                sortValue={sortBy}
                onSortChange={setSortBy}
              />
              {sidebarList}
            </div>
            <div className='styleguide-main'>{detailsPanel}</div>
          </div>
        </div>
        <CopyTigerAlert dialog={dialog} isLoading={isLoading} onHide={() => setDialog(undefined)} />
        {showTrainModal && (
          <TrainModal
            deleteModel={() => deleteModel(selectedModel as InstanceRow)}
            end={end}
            isLoadingTrainingOptions={isLoadingTrainingOptions}
            mode={trainModalMode}
            onDateRangeChange={onDateRangeChange}
            onHide={onTrainModalClose}
            selectedModel={trainModalMode === 'edit' ? selectedModel : undefined}
            setDialog={setDialog}
            setSelectedModelID={setSelectedModelID}
            start={start}
            trainingOptions={trainingOptions}
          />
        )}
      </div>
    </>
  );
};
